import React, { useEffect, useRef, useState } from 'react';
import { useFilterList } from '../../context/FilterListProvider';
import { ExportButton, useLoading, useRefresh, useListContext, useListController, SelectColumnsButton } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { Grid, Stack, Button, LinearProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MSelect from '../controls/MSelect';
import MDatePicker from '../controls/MDatePicker';
import MAutoComplete from '../controls/MAutoComplete';
import MCheckbox from '../controls/MCheckbox';
import InputSearch from '../controls/InputSearch';
import MDateRangePicker from '../controls/MDateRangePicker';
import MNumber from '../controls/MNumber';
import MNumberRange from '../controls/MNumberRange';
import MTextRange from '../controls/MTextRange';
import MInputFilter from '../controls/MInputFilter';
import RaExporter, { RaExporterFile } from './RaExporter';
import MSelectGrouping from '../controls/MSelectGrouping';
import MRadio from '../controls/MRadio';
import MTextField from '../controls/MTextField';
import isEqual from 'lodash/isEqual';
import { RaKeys, getRaStore, setRaStore } from '../../utils/common';
import { formattedDate } from '../controls/MDateControl';
import './style.css';
import MCheckboxGroup from '../controls/MCheckboxGroup';
import MToggleButtonGroup from '../controls/MToggleButtonGroup';

/** `component` values recognized by {@link RenderControls}' `getControl`/`getGridItem`. */
type ControlComponentType =
    | 'text'
    | 'number'
    | 'select'
    | 'selectgrouping'
    | 'multiselect'
    | 'numberrange'
    | 'textrange'
    | 'date'
    | 'daterange'
    | 'autocomplete'
    | 'checkbox'
    | 'checkboxgroup'
    | 'textfilter'
    | 'radio'
    | 'toggle';

/** A single choice entry for select-like controls: `{id, name}` object, or a plain string for `autocomplete`. */
type ControlChoice = { id?: unknown; name?: unknown } | string;

/**
 * Documented shape of a control-config entry, with no index signature. `Omit`/`Pick` only
 * distribute member-by-member over a type like this one; a type whose `keyof` includes
 * `string` (i.e. one with a `[key: string]: unknown` index signature) makes `Omit<T, K>`
 * collapse back to `{ [x: string]: unknown }`, discarding every named member. Keep this base
 * interface free of an index signature so {@link InternalControl} below stays correctly typed;
 * the index signature is added back only on the public {@link ControlConfig} export.
 */
interface ControlConfigBase {
    /** text | number | numberrange | select | multiselect | autocomplete | date | daterange | checkbox | toggle */
    component: ControlComponentType;
    /** name is mapping with field in your table */
    name: string;
    /** Label for control, ifnull then eq name */
    label?: string;
    /** hide control, default: false */
    isHide?: boolean;
    /** array object with [{id, name}];
     * autocomplete only array string ['blue', 'red'] */
    choices?: ControlChoice[];
    /** Object is for default values: {account_eq: 1, ...} */
    defaultValue?: unknown;
    /** For enable null value for checkbox */
    nullable?: boolean;
    /** total digits after int, ex: 10.05 => .05 : 2 digits. Default: 2 */
    decimal?: number;
    /** Range number: ex: min 0, max 100 => [0,100] */
    range?: unknown[];
    /** Range Min of numberrange: ex: min 0, max 100 => [0,100] */
    rangeMin?: unknown[];
    /** Range Max of numberrange: ex: min 0, max 100 => [0,100] */
    rangeMax?: unknown[];
    /** default: false / true: allow input text like phone numer: +84123222, 09123222,.... */
    isTextNumber?: boolean;
    /** Select allow show empty option => default true */
    isEmptyOption?: boolean;
    /** Using with Grid: size={2},.... */
    xs?: number;
    /** Override onChange; called with `{ name, value }` (not the raw event/value). */
    onChange?: (payload: { name: string; value: unknown }) => void;
    /** Override onKeyDown Enter */
    onEnter?: () => void;
    /** For will return value utc, default = false */
    isUTC?: boolean;
    /** For Format for datepicker, default: 'MM/DD/YYYY' */
    format?: string;
}

/** A single control's configuration, as passed by callers in the `controls` array prop. */
export interface ControlConfig extends ControlConfigBase {
    /** Any other prop forwarded as-is to the rendered control component. */
    [key: string]: unknown;
}

/** {@link ControlConfig} after `RenderControls` normalizes it: `component` renamed to `_component`. */
type InternalControl = Omit<ControlConfigBase, 'component'>
    & { _component?: ControlComponentType; [key: string]: unknown };

/** Props for {@link RenderControls}. */
interface RenderControlsProps {
    /** List array control [{component, name, label, xs, ...}]. Default: `[]`. */
    controls?: ControlConfig[];
    /** Use a useState: numberrange: [2,5], autocomplete: ["", ""], select: [{id, name}] */
    defaultValues?: Record<string, unknown>;
    /** Show export button */
    exporter?: boolean;
    /** params ex: {filename: 'abc', columns: ['list columns'], limit: 100 }
     *  Call api "Export" on server
     *  (default limit = total) */
    exportFile?: RaExporterFile;
    /** For search general => only apply search with Number and String */
    inputsSearch?: string[];
    /** Custom action of List react-admin */
    actions?: React.ReactNode;
    /** Override onSearch, default will be call getList in dataprovider */
    onSearch?: (params: Record<string, unknown>) => void;
    /** Default show filter in the same line with component, otherwise is show in newline */
    filterInline?: boolean;
    /** Default show filter button, false will hide button */
    hideFilter?: boolean;
    /** Default allow show progress bar, false will hide */
    hideProgressBar?: boolean;
    /** Ref of button filter */
    refFilter?: React.RefObject<HTMLButtonElement> | null;
    /** position actions at start or end */
    positionActions?: string;
    /** Set styles for List actions */
    actionsStyle?: React.CSSProperties;
    /** Set size filter: small | normal | large */
    sizeFilter?: string;
    /** Set disabled for all controls */
    disabled?: boolean;
    /** Auto calculator (Grid item xs) of actions (buttons). Default: true */
    autoCalXsActions?: boolean;
    /** Default false, if true => auto search with hashUrl (includes # in url) */
    useHashUrl?: boolean;
    /** selectColumn = boolean => hide | show columns is DatagridConfigurable */
    selectColumn?: boolean;
    /** only support for react-admin */
    beforeSearch?: () => void;
    /** only support for react-admin */
    onLoadCompleted?: () => void;
    /** Source for ag-Grid: when use RenderControls with ag-Grid */
    agSource?: string;
    /** Sort for ag-Grid: when use RenderControls with ag-Grid */
    agSort?: unknown;
}

const defaultProps = {
    controls: [] as ControlConfig[],
    defaultValues: {} as Record<string, unknown>,
    exporter: false,
    filterInline: true,
    inputsSearch: [] as string[],
    actions: null,
    refFilter: null,
    sizeFilter: 'small',
    positionActions: 'end',
    hideFilter: false,
    hideProgressBar: false,
    autoCalXsActions: true,
    useHashUrl: false
};

/**
 * Filter/action bar for `RaList`/AG Grid pages: renders one input per entry in `controls`,
 * plus a Filter button and export/custom actions, and drives search either through
 * react-admin's `useListController`/`setFilters` or a caller-supplied `onSearch`.
 */
const RenderControls: React.FC<RenderControlsProps> = ({
    controls = defaultProps.controls,
    actions = defaultProps.actions,
    positionActions = defaultProps.positionActions,
    sizeFilter = defaultProps.sizeFilter,
    defaultValues = defaultProps.defaultValues,
    exporter = defaultProps.exporter,
    hideProgressBar = defaultProps.hideProgressBar,
    inputsSearch = defaultProps.inputsSearch,
    refFilter = defaultProps.refFilter,
    filterInline = defaultProps.filterInline,
    autoCalXsActions = defaultProps.autoCalXsActions,
    useHashUrl = defaultProps.useHashUrl,
    hideFilter = defaultProps.hideFilter,
    actionsStyle, selectColumn, onSearch, agSource, agSort, exportFile, disabled, beforeSearch, onLoadCompleted
}) => {
    let sort, resource, perPage, page, setPage, setPerPage;
    if (!onSearch) {
        ({ sort, resource, perPage, page, setPage, setPerPage } = useListContext());
    }
    const { setFilters } = useListController({ perPage: perPage, filterDefaultValues: { ...defaultValues, isSearch: false }, storeKey: false, disableSyncWithLocation: true });
    let [filterList, setFilterList] = useState<Record<string, unknown>>({});
    const _filterListContext = useFilterList();
    if (_filterListContext) [filterList, setFilterList] = _filterListContext;
    const [toggleFilter, setToggleFilter] = useState(false); // For react-admin
    const [sortPaging, setSortPaging] = useState({ page, perPage, sort });
    const [isChangeSortPaging, setChangeSortPaging] = useState(false);
    const prevSortRef = useRef(sort);
    // For search general include: input text
    const [isOpenSearch, setIsOpenSearch] = useState(false);
    const [hashUrl, setHashUrl] = useState('');
    const isLoading = useLoading();
    const refresh = useRefresh();
    const location = useLocation();
    if (!refFilter) refFilter = useRef(null);

    //When completed search of React-admin, then refresh data
    if (resource) {
        const isCompleteSearch = getRaStore(`${resource}.${RaKeys.isCompleteSearch}`);
        if (isCompleteSearch === true) {
            setRaStore(`${resource}.${RaKeys.isCompleteSearch}`, false);
            setRaStore(`${resource}.${RaKeys.isRefresh}`, false);
            refresh();
            if (onLoadCompleted) onLoadCompleted();
        }
    }

    useEffect(() => {
        if (resource) {
            setRaStore(`${resource}.${RaKeys.isSearch}`, false);
        }
    }, []);

    useEffect(() => {
        let cloneFilter = Object.assign({}, filterList);
        for (let v in defaultValues) {
            let controlName = controls.find(x => x.name === v);
            let isRange = /range$/.test(controlName?._component as string);
            if (isRange && Array.isArray(defaultValues[v])) {
                let p = Object.keys(cloneFilter).filter(x => x === `${v}_ge` || x === `${v}_le`);
                if (p?.length) p.map((m) => delete cloneFilter[m]); // Delete old value
            }
        }
        setFilterList(combineParams({ ...cloneFilter, ...defaultValues }));
    }, [defaultValues]);

    useEffect(() => {
        let isChangePage = page !== sortPaging.page || perPage !== sortPaging.perPage;
        if (!isEqual(sort, sortPaging.sort) || isChangePage) {
            setChangeSortPaging(true);
            setSortPaging({ sort, page, perPage });
            if (isChangePage) {
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
            }
        }
    }, [sort, page, perPage]);

    useEffect(() => {
        let newFilters: Record<string, unknown> = combineParams(filterList);
        newFilters.sortPaging = sortPaging;
        if (isChangeSortPaging) {
            setChangeSortPaging(false);
            setFilters(newFilters);
            const isRefresh = getRaStore(`${resource}.${RaKeys.isRefresh}`);
            // Array sort is only for first default sort, after changed column sort then ignore
            // Fix: !isRefresh && !Array.isArray(sort.field) => !isRefresh
            if (!isRefresh) {
                setRaStore(`${resource}.${RaKeys.isSearch}`, true);
            }
        }
    }, [sortPaging, isChangeSortPaging]);

    useEffect(() => {
        // For the page has hashUrl
        // When second refresh page, not auto search => must click manual filter button
        if (resource && hashUrl && useHashUrl && !isLoading) {
            setFilters(combineParams());
            setTimeout(() => refFilter?.current?.click(), 100);
        }
    }, [hashUrl]);

    useEffect(() => {
        let queryString = location.search ?? location.hash;
        if (resource && queryString && useHashUrl) setHashUrl(queryString);
    }, []);

    useEffect(() => {
        if (prevSortRef.current && (prevSortRef.current.field !== sort.field || prevSortRef.current.order !== sort.order)) {
            prevSortRef.current = sort;
            if (beforeSearch) beforeSearch();
        }
    }, [sort]);

    function setChangePaging (data: Record<string, unknown>) {
        const defaultPaging = { page: 1, perPage: perPage, sort: sort };
        if (page !== defaultPaging.page) {
            setPage(defaultPaging.page);
        }
        if (perPage !== defaultPaging.perPage) {
            setPerPage(defaultPaging.perPage);
        }
        data.sortPaging = defaultPaging;
    }

    const FilterButton: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
        return (
            <Stack style={{ ...style || {}, display: hideFilter ? 'none' : '' }}>
                <Button ref={refFilter} size={sizeFilter as 'small' | 'medium' | 'large'} startIcon={<SearchIcon />} onClick={handleSearch}>Filter</Button>
            </Stack>
        );
    };

    const ActionFilter: React.FC = () => {
        return (
            <Stack style={actionsStyle}
                direction="row"
                spacing={1}>
                {actions}
                {exportFile && (
                    <RaExporter
                        resource={resource || agSource}
                        // disabled={total === 0}
                        exportFile={exportFile}
                        params={{
                            filter: combineParams(onSearch ? getValueFromMui() : getValueSearchFromRA()),
                            pagination: (exportFile.limit || 0) > 0 ? { page: 1, perPage: exportFile.limit as number } : { page: 1, perPage: 1000000 },
                            sort: (sort || agSort) as { field: string; order: string }
                        }}
                    />
                )}
                {exporter && <ExportButton />}
                {selectColumn && <SelectColumnsButton />}
            </Stack>
        );
    };

    disabled = isLoading || disabled;
    let filterStyle: React.CSSProperties = {}, totalXs = 0, actionXs = 1, lastComponent = controls.filter(x => !x.isHide).slice(-1)?.[0];
    //Remove The tag <text>,<number>,<checkboxgroup>,... is unrecognized in this browser => replace 'component' to 'input'
    let inputControls: InternalControl[] = (controls || []).map(x => {
        let { component, ...rest } = x;
        return { ...rest, _component: component };
    });
    return (
        <div style={{ width: '100%' }} className={`rendercontrols ${disabled ? 'disabled' : ''}`}>
            {inputsSearch.length > 0 && (
                <Grid container justifyContent="space-between">
                    <Grid>
                        <InputSearch
                            isOpenSearch={isOpenSearch}
                            setIsOpenSearch={setIsOpenSearch}
                            setFilters={setFilters}
                            inputs={inputsSearch}
                            resource={resource}
                            defaultFilters={defaultValues}
                            // InputSearch's onSearch prop expects an untyped callback; RenderControlsProps.onSearch
                            // is more narrowly typed as (params: Record<string, unknown>) => void.
                            onSearch={onSearch as any}
                        />
                    </Grid>
                </Grid>)}
            {(isOpenSearch || inputsSearch.length === 0) && (
                <Grid container direction="row" rowSpacing={0}
                    justifyContent="flex-start"
                    alignItems="center" spacing={1}>
                    {inputControls.map((control, index) => {
                        if (!control.label) {
                            control.label = control.name;
                            if (control.label) {
                                control.label = String(control.label).charAt(0).toUpperCase() + String(control.label).slice(1); // Uppercase for first character
                            }
                        }
                        if (!control.hasOwnProperty('isHide')) control.isHide = false;
                        if (!control.hasOwnProperty('defaultValue')) {
                            let isHasDefault = defaultValues.hasOwnProperty(control.name);
                            if (isHasDefault) control.defaultValue = defaultValues[control.name];
                        }
                        if (!control.isHide) {
                            let _xs = getGridItem(control);
                            totalXs += _xs;
                            if (totalXs > 12) totalXs = _xs;

                            if (lastComponent && lastComponent.name === control.name) {
                                if (!['checkbox', 'radio'].some(x => x === control._component)) filterStyle = { marginTop: 20 };
                            }
                            actionXs = 12 - totalXs - 0.5; // - 0.5 mean ignore Filter button
                            return (
                                <Grid size={_xs} key={index}>
                                    {getControl(control)}
                                </Grid>
                            );
                        }
                    })}
                    {filterInline && (
                        <>
                            <Grid size={0.5} style={filterStyle}>
                                <FilterButton />
                            </Grid>
                            {autoCalXsActions && (
                                <Grid container justifyContent='flex-end' size={actionXs} style={filterStyle}>
                                    <ActionFilter />
                                </Grid>
                            )}
                            {!autoCalXsActions && (
                                <Grid container spacing={1} style={filterStyle}>
                                    <ActionFilter />
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            )}
            {!filterInline && (
                <Stack
                    direction="row"
                    justifyContent={`flex-${positionActions}`}
                    alignItems="flex-start"
                    spacing={1}
                    style={{ marginBottom: '5px' }}>
                    <FilterButton style={actionsStyle} />
                    <ActionFilter />
                </Stack>
            )}
            {disabled && !hideProgressBar && <LinearProgress color="secondary" />}
        </div>
    );

    function combineParams (params?: Record<string, unknown>): Record<string, unknown> {
        let newObj: Record<string, unknown> = {}, newParams = params || defaultValues;
        for (let p in newParams) {
            let item = newParams[p];
            // numberrange or daterange, but ignore autocomplete
            let controlName = controls.find(x => x.name === p) as InternalControl | undefined;
            if (controlName?.component) controlName._component = controlName.component as ControlComponentType;
            let isRange = /range$/.test(controlName?._component ?? '');
            if (isRange && Array.isArray(item)) {
                let isDateRange = controlName?._component === 'daterange';
                let isTime = isDateRange && controlName?.format?.includes(':');
                // `item`'s elements come from a Record<string, unknown> filter value, cast for formattedDate's params.
                if (item[0]) newObj[`${p}_ge`] = isDateRange ? formattedDate(item[0] as any, controlName?.isUTC, isTime) : item[0];
                if (item[1]) newObj[`${p}_le`] = isDateRange ? formattedDate(item[1] as any, controlName?.isUTC, isTime, true) : item[1];
            } else if (controlName?._component === 'checkboxgroup') {
                if (Array.isArray(item)) newObj[p] = JSON.stringify(item);
                else newObj[p] = item;
            } else if (controlName?._component === 'date') {
                // `item` is a Record<string, unknown> filter value narrowed for formattedDate's `MDateValue` param.
                newObj[p] = formattedDate(item as any, controlName?.isUTC, controlName?.format?.includes(':'));
            } else if (Array.isArray(item)) { // for array object or array => to get array
                newObj[p] = item.map(x => x.id !== undefined ? x.id : x);
            } else if (p === 'undefined' || item === null || (typeof item === 'string' && item.trim() === ''))
                continue;
            else newObj[p] = item;
        }
        return newObj;
    }

    function getValueSearchFromRA (): Record<string, unknown> {
        let dataSearch = filterList;
        for (const [key, value] of Object.entries(dataSearch)) {
            if (key === 'undefined' || value === null || (typeof value === 'string' && value.trim() === ''))
                delete dataSearch[key];
        }
        return dataSearch;
    }

    function getValueFromMui (): Record<string, unknown> {
        return filterList;
    }

    function handleSearchReactAdmin () {
        let dataSearch = getValueSearchFromRA();
        // toggle to allow search, because state not update when state not changed => allow click search even not change
        dataSearch._toggleFilter = toggleFilter;
        setToggleFilter(!toggleFilter);
        let _newData = combineParams(dataSearch);
        // Reset paging for search
        setChangePaging(_newData);
        if (setFilters) setFilters(_newData);
        setRaStore(`${resource}.isSearch`, true);
    }

    function handleSearchMui () {
        let dataSearch = getValueFromMui();
        delete dataSearch['_toggleFilter'];
        if (resource) setRaStore(`${resource}.${RaKeys.isSearch}`, true);
        onSearch!(combineParams(dataSearch));
    }

    function handleSearch () {
        if (beforeSearch) beforeSearch();
        if (onSearch) handleSearchMui();
        else handleSearchReactAdmin();
    }

    function handleChange (control: InternalControl, e: unknown, filterName?: string) {
        let { name, onChange } = control;
        let value = e;
        if ((e as { target?: { value: unknown } })?.target) value = (e as { target: { value: unknown } }).target.value;
        let prevSource = combineParams(filterList);
        if (filterName && !/range$/.test(control._component ?? '')) {
            for (let key in prevSource) {
                if (key.includes(name))
                    delete prevSource[key];
            }
        }
        name = filterName || name;
        if (value === null || (typeof value === 'string' && value.trim() === '')) delete prevSource[name];
        else prevSource[name] = value;
        setFilterList(prevSource);
        if (onChange) onChange({ name, value });
    }

    function handleKeyDown (event: React.KeyboardEvent) {
        if (event.key === 'Enter') handleSearch();
    }

    function getGridItem (control: InternalControl): number {
        if (control.xs) return control.xs;
        switch (control._component) {
            case 'text':
            case 'multiselect':
            case 'autocomplete':
            case 'daterange':
                return 2;
            default: return 1;
        }
    }

    function getControl (control: InternalControl): React.ReactElement {
        // Remove Warning: React does not recognize the `isHide` prop on a DOM element.
        delete control.isHide;
        // render
        // Below, `{...control as any}` is required for every case: `control` is a generic
        // InternalControl (choices/range/defaultValue typed loosely as `unknown`/`unknown[]` to
        // cover every control kind), while each M* control declares a narrower, control-specific
        // prop type (e.g. MAutoComplete's `choices: MAutoCompleteOption[]`, MNumber's
        // `range: [number, number]`). The cast is safe: callers supply `ControlConfig` entries
        // matching the `component` they choose, and each `case` here already dispatches on that.
        switch (control._component) {
            case 'text':
                return <MTextField {...control as any} onChange={(e: unknown) => handleChange(control, e)} onKeyDown={handleKeyDown} />;
            case 'number':
                return <MNumber {...control as any} onChange={(e: unknown) => handleChange(control, e)} onKeyDown={handleKeyDown} />;
            case 'select':
                return <MSelect {...control as any} onChange={(e: unknown) => handleChange(control, e)} />;
            case 'selectgrouping':
                return <MSelectGrouping {...control as any} onChange={(e: unknown) => handleChange(control, e)} />;
            case 'numberrange':
                return <MNumberRange {...control as any} onChange={(e: unknown, params: string) => handleChange(control, e, params)} />;
            case 'textrange':
                return <MTextRange {...control as any} onChange={(e: unknown, params: string) => handleChange(control, e, params)} />;
            case 'date':
                return <MDatePicker {...control as any} onChange={(e: unknown) => handleChange(control, e)} />;
            case 'daterange':
                return <MDateRangePicker {...control as any} onChange={(e: unknown, params: string) => handleChange(control, e, params)} />;
            case 'autocomplete':
                return <MAutoComplete {...control as any} onChange={(e: unknown) => handleChange(control, e)} />;
            case 'checkbox':
                return <MCheckbox {...control as any} onChange={(e: unknown) => handleChange(control, e)} style={{ paddingTop: 10 }} />;
            case 'checkboxgroup':
                return <MCheckboxGroup {...control as any} onChange={(e: unknown) => handleChange(control, e)} />;
            case 'textfilter':
                return <MInputFilter {...control as any} inputProps={{ autoComplete: 'on' }} onChange={(e: unknown, params: string) => handleChange(control, e, params)} onKeyDown={handleKeyDown} />;
            case 'radio':
                return <MRadio {...control as any} onChange={(e: unknown, _params: unknown) => handleChange(control, e)} />;
            case 'toggle':
                return <MToggleButtonGroup {...control as any} onChange={(e: unknown) => handleChange(control, e)} />;
            default:
                return <></>;
        }
    }
};

export default React.memo(RenderControls);
